
import React from 'react';
import { useAudio } from './AudioContext';
import { 
  Play, Pause, SkipForward, SkipBack, X, Volume2, 
  Smartphone, Activity, Globe, Headphones, Mic2
} from 'lucide-react';
import { AdUnit } from './AdUnit';
import { useThemeTokens } from '../hooks/useActiveTheme';

export const GlobalAudioPlayer: React.FC = () => {
  const { currentTrack, isPlaying, progress, duration, togglePlay, stopAudio, seek, setVolume, jump } = useAudio();
  const tokens = useThemeTokens();

  if (!currentTrack) return null;

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div 
      role="region" 
      aria-label="Global Audio Player" 
      className="fixed bottom-0 left-0 right-0 lg:left-64 z-[150] animate-slide-up px-4 pb-3 md:px-6 md:pb-6 pointer-events-none"
    >
      <div className="max-w-4xl mx-auto pointer-events-auto space-y-2 w-full">
        <div className="w-full overflow-hidden">
          <AdUnit placement="podcasts_player_sticky" />
        </div>
        <div className="bg-slate-950/95 backdrop-blur-2xl rounded-[1.5rem] md:rounded-[3rem] border border-white/10 p-3 md:p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col md:flex-row items-center gap-3 md:gap-8 relative overflow-hidden">
          {/* Subtle Background Glow */}
          <div className="absolute top-0 left-0 w-1/2 h-full bg-[radial-gradient(circle_at_left,rgba(14,165,233,0.05)_0%,transparent_100%)] pointer-events-none"></div>
          
          {/* Track Info - Constrained width and wrapping logic */}
          <div className="flex items-center gap-3 md:gap-4 w-full md:w-auto md:max-w-[240px] lg:max-w-[320px] relative z-10 shrink-0">
            <div className="w-10 h-10 md:w-14 md:h-14 shrink-0 rounded-xl overflow-hidden shadow-xl border-2 border-white/5 bg-slate-800">
              <img src={currentTrack.img} className="w-full h-full object-cover" alt={currentTrack.title || "Track art"} />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white font-black text-xs lg:text-sm line-clamp-2 tracking-tight leading-tight">
                {currentTrack.title}
              </p>
              <div className="flex items-center gap-1.5 mt-0.5 md:mt-1">
                <div className={`w-1 h-1 rounded-full bg-emerald-500 ${isPlaying ? 'animate-pulse' : 'opacity-40'}`}></div>
                <p className={`${tokens.accentText} text-[7px] md:text-[9px] font-black tracking-widest`}>Registry Sync</p>
              </div>
            </div>
            {/* Mobile-only Close */}
            <button onClick={stopAudio} aria-label="Close audio player" className="md:hidden p-2 text-slate-500 hover:text-white">
              <X size={16} />
            </button>
          </div>

          {/* Controls & Progress */}
          <div className="flex-1 w-full flex flex-col gap-2 md:gap-4 relative z-10">
            <div className="flex items-center gap-4 md:gap-6 justify-center">
              <button className="text-white/20 hover:text-white transition-all hidden sm:block" onClick={() => jump(-15)} title="Rewind 15s" aria-label="Rewind 15 seconds">
                <SkipBack size={18} />
              </button>
              
              <button 
                onClick={togglePlay} 
                aria-label={isPlaying ? "Pause audio" : "Play audio"}
                className={`w-10 h-10 md:w-14 md:h-14 ${tokens.primaryBtn} text-white rounded-full flex items-center justify-center shadow-xl hover:scale-105 transition-all active:scale-95 group`}
              >
                {isPlaying ? <Pause size={20} className="md:w-6 md:h-6" /> : <Play size={20} fill="currentColor" className="ml-0.5 md:w-6 md:h-6" />}
              </button>
              
              <button className="text-white/20 hover:text-white transition-all hidden sm:block" onClick={() => jump(15)} title="Forward 15s" aria-label="Fast forward 15 seconds">
                <SkipForward size={18} />
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <span className="text-[8px] md:text-[9px] font-black text-slate-500 tabular-nums w-8">
                {formatTime(progress)}
              </span>
              <div 
                role="slider"
                aria-label="Playback track progress"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={duration || 100}
                className="flex-1 h-1 md:h-1.5 bg-white/5 rounded-full overflow-hidden relative group/bar cursor-pointer" 
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const pos = (e.clientX - rect.left) / rect.width;
                  seek(pos * duration);
                }}
              >
                <div 
                  className={`h-full ${tokens.progressBar} transition-all duration-300 relative`} 
                  style={{ width: `${(progress / (duration || 1)) * 100}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-2 h-2 md:w-3 md:h-3 bg-white rounded-full shadow-lg scale-0 group-hover/bar:scale-100 transition-transform"></div>
                </div>
              </div>
              <span className="text-[8px] md:text-[9px] font-black text-slate-500 tabular-nums w-8 text-right">
                {currentTrack.duration || formatTime(duration)}
              </span>
            </div>
          </div>

          {/* Desktop Volume & Close */}
          <div className="hidden md:flex items-center gap-6 relative z-10 shrink-0">
            <div className="flex items-center gap-3">
              <Volume2 className="text-slate-600" size={18} />
              <input 
                type="range" min="0" max="1" step="0.1" 
                defaultValue="1"
                aria-label="Volume level"
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-16 h-1 bg-white/10 rounded-full appearance-none accent-indigo-500 cursor-pointer" 
              />
            </div>
            <button onClick={stopAudio} aria-label="Close audio player" className="p-3 bg-white/5 text-slate-500 hover:bg-rose-600 hover:text-white rounded-2xl transition-all group/close">
               <X size={20} className="group-hover/close:rotate-90 transition-transform" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
